import { useEffect, useRef, useState } from 'react';
import { callMediaUrl } from '../api';
import type { Call } from '../types';

interface Props {
  call: Call;
  token: string;
}

/** A camera or shared screen, together with the voice that belongs to it. */
interface View {
  label: string;
  videoIndex: number;
  audioIndex?: number;
  /** Milliseconds the audio starts after the video; usually a few dozen. */
  audioOffsetMs: number;
}

/**
 * The recorder writes one file per track, so a camera has no sound of its own.
 * A watchable view is therefore a pair: the picture, and the microphone of the
 * person it belongs to. Both come from the same recording window, which is why
 * they can simply be played together.
 */
function viewsOf(call: Call): View[] {
  const media = call.media ?? [];
  const views: View[] = [];

  media.forEach((item, videoIndex) => {
    if (item.kind !== 'video') return;

    // The same participant's microphone. Screen shares carry their own audio
    // track when the shared application made a sound.
    const wanted = item.source === 'screen_share' ? 'screen_share_audio' : 'microphone';
    const audio = media.findIndex(
      (candidate) =>
        candidate.kind === 'audio' &&
        candidate.participant === item.participant &&
        candidate.source === wanted &&
        // Overlapping in time, so a participant who rejoined is not paired
        // with the microphone from their earlier visit.
        candidate.startedAtMs < item.endedAtMs &&
        candidate.endedAtMs > item.startedAtMs,
    );

    views.push({
      label:
        item.source === 'screen_share'
          ? `${item.participant} · Bildschirm`
          : item.participant,
      videoIndex,
      audioIndex: audio >= 0 ? audio : undefined,
      audioOffsetMs: audio >= 0 ? media[audio]!.startedAtMs - item.startedAtMs : 0,
    });
  });

  return views;
}

/** Beyond this the two are audibly apart, and nudging is better than drifting. */
const MAX_DRIFT_SECONDS = 0.3;

export function CallPlayer({ call, token }: Props) {
  const views = viewsOf(call);
  const [chosen, setChosen] = useState(0);
  const [sources, setSources] = useState<{ video?: string; audio?: string }>({});
  const [error, setError] = useState<string>();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const view = views[chosen];

  useEffect(() => {
    if (!view) return;
    let cancelled = false;
    setSources({});
    setError(undefined);

    (async () => {
      const [video, audio] = await Promise.all([
        callMediaUrl(token, call.roomId, view.videoIndex),
        view.audioIndex === undefined
          ? Promise.resolve(undefined)
          : callMediaUrl(token, call.roomId, view.audioIndex),
      ]);
      if (!cancelled) setSources({ video: video.url, audio: audio?.url });
    })().catch((err: unknown) => {
      if (!cancelled) setError(err instanceof Error ? err.message : 'Wiedergabe nicht möglich');
    });

    return () => {
      cancelled = true;
    };
  }, [token, call.roomId, view?.videoIndex, view?.audioIndex]);

  /**
   * The picture leads and the sound follows.
   *
   * People scrub the video, so it owns the clock. The audio is corrected only
   * when it has drifted audibly - setting currentTime on every frame would
   * stutter.
   */
  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video || !audio || !sources.audio) return;

    const offset = (view?.audioOffsetMs ?? 0) / 1000;
    const align = () => {
      const wanted = video.currentTime - offset;
      if (Math.abs(audio.currentTime - wanted) > MAX_DRIFT_SECONDS) audio.currentTime = wanted;
    };

    const onPlay = () => {
      align();
      void audio.play().catch(() => undefined);
    };
    const onPause = () => audio.pause();
    const onRate = () => (audio.playbackRate = video.playbackRate);

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('seeked', align);
    video.addEventListener('timeupdate', align);
    video.addEventListener('ratechange', onRate);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('seeked', align);
      video.removeEventListener('timeupdate', align);
      video.removeEventListener('ratechange', onRate);
    };
  }, [sources.video, sources.audio, view?.audioOffsetMs]);

  if (views.length === 0) return <p className="muted small">Keine Bildspur aufgezeichnet.</p>;

  return (
    <div className="call-player">
      <div className="call-views">
        {views.map((candidate, index) => (
          <button
            key={`${candidate.videoIndex}`}
            className={index === chosen ? 'chosen' : ''}
            onClick={() => setChosen(index)}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}

      {sources.video ? (
        <>
          {/* Muted on purpose: the file has no sound, and the audio element
              beside it is where the voice comes from. */}
          <video ref={videoRef} src={sources.video} controls muted playsInline preload="metadata" />
          {sources.audio ? (
            <audio ref={audioRef} src={sources.audio} preload="metadata" />
          ) : (
            <p className="muted small">Zu dieser Bildspur wurde kein Ton aufgezeichnet.</p>
          )}
        </>
      ) : (
        !error && <p className="muted small">Wiedergabe wird vorbereitet …</p>
      )}
    </div>
  );
}
